<template>
  <v-container fluid class="px-0 pt-0">
    <v-row>
      <v-col cols="12" class="pb-0">
        <MediaModeForm v-if="(currentUser.clientType === 'Media' || currentUser.clientType === 'Driblab') && !hideMedia" :fields="mediaModeFields"
        :mediaMode="mediaMode" @mediaModeChange="onMediaModeChange($event)"
        :mediaModeData="mediaModeData" @mediaModeDataChange="onMediaModeDataChange($event)" selectorMode></MediaModeForm>
      </v-col>

      <!-- if phase === 0 means is whole game flow -->
      <v-col v-if="xTText && phase === 0" cols="12" class="px-4 body-1">
        <div id="gameFlowText" v-html="xTTextCompact"></div>
      </v-col>

      <!-- If mobile device, set this column second -->
      <v-col cols="12" lg="5" :class="phase > 0 ? 'px-0 py-0' : 'px-0'" :order="$vuetify.breakpoint.smAndDown ? 2 : 1">
        <v-col v-if="phase > 0" cols="12" class="py-0 mb-3">
          <div class="rounded text-center pa-2 pt-3" :class="$vuetify.theme.dark ? 'secondary' : 'primary'">
            <span class="white--text header-font" style="font-size: 18px;">{{phasesHeaderText}}</span>
          </div>
        </v-col>
        <v-col cols="12" class="py-0">
          <v-data-table
            id="gameFlowTable"
            :headers="gameFlowDataHeader"
            :items="gameFlowDataPerPhasesItems"
            :items-per-page="itemsPerPage"
            class="elevation-1"
            dense
            hide-default-footer
          >
            <template v-for="(h,index) in gameFlowDataHeader" v-slot:[`header.${h.value}`]="{ header }">
              <v-tooltip top color="lightSecondary lighten-4" max-width="300" :key="index">
                <template v-slot:activator="{ on }">
                  <span v-on="on" v-bind:class="{'px-2':index === 0}">{{h.text}}</span>
                </template>
                <span class="black--text body-1">{{h.tooltip}}</span>
              </v-tooltip>
            </template>
            <template v-slot:item.teamId="{ item }">
              <div class="pl-2 my-2">
                <router-link
                  :to="'/team/' + item.teamId"
                  class="nav-link d-flex align-center w-100"
                >
                  <img
                    :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + item.teamId + '.png'"
                    style="height: 20px; width: 20px;"
                  />
                  <span class="pl-1 body-1" :class="$vuetify.theme.dark ? 'primary--text' : 'secondary--text text--darken-2'"> {{ item.teamName }} </span>
                </router-link>
              </div>
            </template>
            <template v-slot:item.period="{ item }">
              <span v-if="item.period > 0"> {{ item.minFrom }}' - {{ item.minTo }}' ({{ item.period }})</span>
              <span v-else> {{ item.minFrom }}' - {{ item.minTo }}' (1-2)</span>
            </template>
            <template v-slot:item.possession="{ item }">
              <span> {{ toRound((item.possession * 100), 1) }}%</span>
            </template>
            <template v-slot:item.xT="{ item }">
              <div class="w-100 h-100 d-flex align-center justify-center font-weight-bold" v-bind:style="{'background-color':getBackgroundColor(item,'xT')}"
                v-bind:class="getTextColorClass(item,'xT')">
                <span> {{ toRound(item.xT, 3) }}</span>
              </div>
            </template>
            <template v-slot:item.passesFinalThird="{ item }">
              <span> {{ item.passesFinalThird }}</span>
            </template>
            <template v-slot:item.xG="{ item }">
              <div class="w-100 h-100 d-flex align-center justify-center font-weight-bold" v-bind:style="{'background-color':getBackgroundColor(item,'xG')}"
                v-bind:class="getTextColorClass(item,'xG')">
                <span> {{ toRound(item.xG, 2) }}</span>
              </div>
            </template>
          </v-data-table>
        </v-col>
        <!-- if phase > 0 means is post game analysis, game flow per phases -->
        <v-col v-if="xTText && phase > 0" cols="12">
          <div style="text-align:justify" class="body-1" v-html="xTText"></div>
        </v-col>
      </v-col>
      <!-- If mobile device, set this column first -->
      <v-col cols="12" lg="7" :class="phase > 0 ? 'pl-md-0 pb-5 pt-0' : 'pl-md-0'" :order="$vuetify.breakpoint.smAndDown ? 1 : 2">
        <div class="d-flex flex-column h-100 py-">
          <v-card
            class="card-chart h-100 rounded-b-0"
            elevation="0" outlined
            :dark="$vuetify.theme.dark">
            <template>
              <v-row  v-if="!mediaMode">
                <v-col cols="12" xl="8" class="d-flex align-center pl-7">
                  <v-icon color="secondary" large class="mr-2">stacked_bar_chart</v-icon>
                  <div class="d-flex flex-column">
                    <span class="primary--text monospace-font body-1 font-weight-medium">{{ xTChart.title }}</span>
                    <div v-if="game" class="d-flex align-center">
                      <v-img
                        :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + game.homeTeamId + '.png'"
                        :height="20"
                        :width="20"
                        contain
                        v-on:error="onImgError($event,item)"
                      />
                      <span class="primary--text mx-1 body-2 subheader-font">
                        {{ game.homeTeamName }}
                      </span>
                      <div class="text-center text--darken-2 body-2 subheader-font">
                        <span>{{game.homeTeamScore}}</span>
                        <span class="mx-1">-</span>
                        <span>{{game.awayTeamScore}}</span>
                      </div>
                      <span class="primary--text mx-1 body-2 subheader-font">
                        {{ game.awayTeamName }}
                      </span>
                      <v-img
                        :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + game.awayTeamId + '.png'"
                        :height="20"
                        :width="20"
                        contain
                        class="ml-1"
                        v-on:error="onImgError($event,item)"
                      />
                    </div>
                    <div v-if="game" class="d-flex justify-space-between px-6 mt-n1">
                      <span class="primary--text body-2 subheader-font">
                        {{ game.homeTeamFormation }}
                      </span>
                      <span class="primary--text body-2 subheader-font pr-1">
                        {{ game.awayTeamFormation }}
                      </span>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </template>
            <GameAvatarMedia v-if="avatarSize && mediaMode && xTChart.data.datasets[0].data.length && game" :width="avatarSize.width" :height="avatarSize.height" :id="`${gameId}-${chartId}`" :game="game"
              :titleLeft="mediaModeData.titleLeft" :titleRight="mediaModeData.titleRight"
              :leftColor="mediaModeData.leftBorderColor"
              :rightColor="mediaModeData.rightBorderColor"
              ref="gameAvatarMediaRef" :bgColor="mediaModeData.bgColor" :textColor="mediaModeData.textColor"
              type="gameFlow" :lineDistance="mediaModeData.lineLength" :avatarSize="mediaModeData.avatarSize" :lineWeight="mediaModeData.lineWeight"
              :titleFontSize="mediaModeData.titleFontSize" :titleFontWeight="mediaModeData.titleFontWeight"
              :subtitle="mediaModeData.subtitle" :subtitleFontSize="mediaModeData.subtitleFontSize" :subtitleFontWeight="mediaModeData.subtitleFontWeight"
              :subtitle2="mediaModeData.subtitle2" :subtitle2FontSize="mediaModeData.subtitle2FontSize" :subtitle2FontWeight="mediaModeData.subtitle2FontWeight"
              class="mt-5"
              ></GameAvatarMedia>
            <ZeroLineChart v-if="xTChart.data.datasets[0].data.length > 0"
              ref="xTChartRef"
              :datasets="xTChart.data.datasets"
              :xAxisLabels="xTChart.data.labels"
              :options="xTChart.options"
              :mediaMode="mediaMode"
              :id="`${id}${gameId}-${chartId}`"
              @sizeChange="onChartSizeChange($event)"
              @loaded="onLoaded($event)"
              ></ZeroLineChart>
            <DriblabSocialMedia v-if="game && avatarSize && mediaMode && mediaModeData.socialMedia"  :width="avatarSize.width" :height="50" :id="`${gameId}-${chartId}`"
              ref="driblabSocialMediaRef" :bgColor="mediaModeData.bgColor" :textColor="mediaModeData.textColor" class="mt-n3"
              :fontSize="mediaModeData.socialMediaFontSize" :fontWeight="mediaModeData.socialMediaFontWeight"
              :logoCfg="mediaModeData.logo" :sponsor="mediaModeData.sponsor.dataUrl" type="gameFlow"></DriblabSocialMedia>
          </v-card>
          <v-card elevation="0" outlined color="transparent" v-if="xTChart.data.datasets[0].data.length > 0 && !hideMedia" class="d-flex justify-end py-2 px-0 rounded-t-0">
              <v-btn color="secondary" class="elevation-0 monospace-font white--text px-3 mx-1" rounded small
                :x-small="!$vuetify.breakpoint.xsOnly"
                v-on:click.native="saveAsPNG()"
              >
                <v-icon small class="mr-1">
                  file_download</v-icon
                >
                <span class="">PNG</span>
              </v-btn>
              <v-btn color="secondary" class="elevation-0 monospace-font white--text px-3 mx-1" rounded small
                v-on:click.native="saveAsSVG()"
              >
                <v-icon small
                  >file_download</v-icon
                >
                <span>SVG</span>
              </v-btn>
              <v-btn color="secondary" class="elevation-0 monospace-font white--text px-3 mx-1" rounded small
                :x-small="!$vuetify.breakpoint.xsOnly"
                :loading="loadingExport"
                :disabled="!chartSize"
                v-on:click.native="onGenerateIFrameClick()"
              >
                <v-icon small class="mr-1"
                  >share</v-icon
                >
                <span>Embed</span>
              </v-btn>
          </v-card>
        </div>
      </v-col>
    </v-row>
    <ChartExportLinksModal ref="chartExportLinksModalRef" :title="`${game.homeTeamName} ${game.homeTeamScore} - ${game.awayTeamScore} ${game.awayTeamName}`" v-if="game"
    :subtitle="$t('game.header.gameFlow')"></ChartExportLinksModal>
  </v-container>
</template>

<script>
import ZeroLineChart from "@/views/charts/ZeroLineChart";
import GameAvatarMedia from './GameAvatarMedia';
import DriblabSocialMedia from '../base/DriblabSocialMedia';
import MediaModeForm from '../base/MediaModeForm';
import {exportSvg, downloadPng, drawImage, copyAndAppendElement, loadFonts} from '../../utils/exportToPNG';
import {round, userHasRole} from '../../utils/functions';
import ChartExportLinksModal from '../base/ChartExportLinksModal';
import { v4 } from 'uuid';
import UserChartService from '../../services/user-chart.service';
import DocumentService from '../../services/document.service';
import UserService from '../../services/user.service';
import Dictionary from '../../dictionary';

const NODE_ENV = process.env.NODE_ENV;
const PROTOCOL = process.env.PROTOCOL || 'http://';
const END_POINT_URL = process.env.END_POINT_URL || window.location.hostname;
const END_POINT_PORT = process.env.END_POINT_PORT || '8081';

const API_URL = NODE_ENV === 'production' ? 'https://pro.driblab.com/embed-chart/' : PROTOCOL + END_POINT_URL + ':' + END_POINT_PORT + '/embed-chart/'

export default {
  name: 'GameFlowChart',
  components: {
    ZeroLineChart,
    GameAvatarMedia,
    DriblabSocialMedia,
    MediaModeForm,
    ChartExportLinksModal
  },
  props: {
    id: {
      type: String,
      required: false,
      default: "xTChart"
    },
    game: {
      type: Object,
      required: true,
    },
    xTChart: {
      type: Object,
      required: true,
    },
    gameFlowDataPerPhasesItems: {
      type: Array,
      required: true,
    },
    itemsPerPage: {
      type: Number,
      required: false,
      default: 12
    },
    chartData: {
      type: Array,
      required: true,
    },
    phase: {
      type: Number,
      required: false,
      default: 0
    },
    totalNumberOfPhases: {
      type: Number,
      required: false,
      default: -1
    },
    phasesHeaderText: {
      type: String,
      required: false,
    },
    chartId:{
      type: Number,
      default: 0
    },
    hideMedia:{
      type: Boolean,
      default: false
    },
    language: {
        type: String,
        required: false
    }
  }, //End props
  data() {
    return {
      gameFlowDataHeader: [],
      homeColor: "#3366FF", //Same blue as xT chart home team
      awayColor: "#000000",
      xTText: "",
      mediaMode: false,
      mediaModeData: {leftBorderColor:"#3366FF",rightBorderColor:"#000000",bgColor:"#ffffff0",socialMedia:true,textColor:"#000000",logo:"color",
                      titleFontSize:"18",titleFontWeight:"900",subtitleFontSize:"14",subtitleFontWeight:"500",lineWeight:"2",lineLength:"180",avatarSize:"64"
                      ,subtitle2FontSize:"12",subtitle2FontWeight:"300",sponsor:{}},
      mediaModeFields: [
        {type:"color",label:"mediaMode.borderColorX",key:"leftBorderColor",labelParams:[1],home:true},
        {type:"color",label:"mediaMode.borderColorX",key:"rightBorderColor",labelParams:[2],away:true},
        {type:"text",label:"mediaMode.titleX",key:"titleLeft",labelParams:[1],home:true},
        {type:"text",label:"mediaMode.titleX",key:"titleRight",labelParams:[2],away:true},
        {type:"number",label:"mediaMode.titleFontSize",key:"titleFontSize",force:true},
        {type:"radio",label:"mediaMode.titleFontWeight",key:"titleFontWeight"
          ,options:[{value:"100",label:"100"},{value:"300",label:"300"},{value:"400",label:"400"},{value:"500",label:"500"},{value:"700",label:"700"},{value:"900",label:"900"}],force:true},
        {type:"text",label:"mediaMode.subtitle",key:"subtitle"},
        {type:"number",label:"mediaMode.subtitleFontSize",key:"subtitleFontSize"},
        {type:"radio",label:"mediaMode.subtitleFontWeight",key:"subtitleFontWeight"
          ,options:[{value:"100",label:"100"},{value:"300",label:"300"},{value:"400",label:"400"},{value:"500",label:"500"},{value:"700",label:"700"},{value:"900",label:"900"}]},
        {type:"text",label:"mediaMode.subtitleX",key:"subtitle2",labelParams:[2]},
        {type:"number",label:"mediaMode.subtitleFontSizeX",key:"subtitle2FontSize",labelParams:[2]},
        {type:"radio",label:"mediaMode.subtitleFontWeightX",key:"subtitle2FontWeight",labelParams:[2]
          ,options:[{value:"100",label:"100"},{value:"300",label:"300"},{value:"400",label:"400"},{value:"500",label:"500"},{value:"700",label:"700"},{value:"900",label:"900"}]},
        {type:"color",label:"mediaMode.textColor",key:"textColor"},
        {type:"color",label:"mediaMode.bgColor",key:"bgColor"},
        {type:"number",label:"mediaMode.avatarLineLength",key:"lineLength",force:true},
        {type:"number",label:"mediaMode.avatarLineWeight",key:"lineWeight",force:true},
        {type:"number",label:"mediaMode.avatarSize",key:"avatarSize",force:true},
        {type:"switch",label:"mediaMode.socialMedia",key:"socialMedia"},
        {type:"number",label:"mediaMode.socialMediaFontSize",key:"socialMediaFontSize",force:true},
        {type:"radio",label:"mediaMode.socialMediaFontWeight",key:"socialMediaFontWeight"
          ,options:[{value:"100",label:"100"},{value:"300",label:"300"},{value:"400",label:"400"},{value:"500",label:"500"},{value:"700",label:"700"},{value:"900",label:"900"}],force:true},
        {type:"image",label:"mediaMode.sponsor",key:"sponsor",force:true,aspectRatio:16/9,imageRestriction:'none',resizable:true,stencilSize:{width:300,height:300}}
      ],
      chartSize: null,
      loadingExport:false
    }
  },
  computed: {
    currentUserLanguage() {
      return this.language ? this.language : this.$store.getters["auth/language"];
    },
    currentUser() {
      return this.$store.state.auth.user;
    },
    avatarSize(){
      return this.chartSize ?  {
          width: this.chartSize.width,
          height: this.chartSize.height / 2.5
      } : null
    },
    gameId(){
      return this.game.gameId;
    },
    xTTextCompact(){
      return this.xTText ? this.xTText.replaceAll("br",' ') : ""
    }
  },
  watch: {
    "mediaMode": function(newVal, oldVal) {
      if(!this.mediaMode){
        this.mediaModeData = {leftBorderColor:"#3366FF",rightBorderColor:"#000000",bgColor:"#ffffff0",socialMedia:true,textColor:"#000000",logo:"color",
                      titleFontSize:"18",titleFontWeight:"900",subtitleFontSize:"14",subtitleFontWeight:"500",lineWeight:"2",lineLength:"180",avatarSize:"64",
                      subtitle2FontSize:"12",subtitle2FontWeight:"300",sponsor:{}}
        this.mediaModeData.subtitle = this.game.leagueName;
        this.mediaModeData.subtitle2 = this.$t("game.header.gameFlow");
        this.mediaModeData.titleLeft = this.game.homeTeamName;
        this.mediaModeData.titleRight = this.game.awayTeamName;

        this.mediaModeData.sponsor.src = require(`@/assets/img/driblab_normal_color.png`);
        this.mediaModeData.sponsor.fallBackSrc = require(`@/assets/img/driblab_normal_color.png`);
      }
      this.mediaModeData.bgColor = this.mediaMode ? '#ffffff' : '#ffffff0';
      this.onWindowResize(null,false)
    }
  },
  mounted: function() {
    this.mediaMode = (this.currentUser.mediaMode || this.currentUser.clientType === 'Media') && !this.hideMedia;
    this.gameFlowDataHeader = [
      { text: Dictionary[this.currentUserLanguage]["table"]["gameFlow"]["team"], tooltip: this.$t("table.gameFlow.team"), width: "35%", align: 'start', value: 'teamId', sortable: true },
      { text: Dictionary[this.currentUserLanguage]["table"]["gameFlow"]["period"], tooltip: this.$t("table.gameFlow.period"), width: "15%", align: 'center', value: 'period', sortable: true },
      { text: Dictionary[this.currentUserLanguage]["table"]["gameFlow"]["possession"], tooltip: this.$t("table.gameFlow.possessionTooltip"), width: "15%", align: 'center', value: 'possession', sortable: true },
      { text: Dictionary[this.currentUserLanguage]["table"]["gameFlow"]["xT"], tooltip: this.$t("table.gameFlow.xTTooltip") , width: "15%", align: 'center', value: 'xT', sortable: true },
      { text: Dictionary[this.currentUserLanguage]["table"]["gameFlow"]["pft"], tooltip: this.$t("table.gameFlow.pftTooltip") , width: "10%", align: 'center', value: 'passesFinalThird', sortable: true },
      { text: Dictionary[this.currentUserLanguage]["table"]["gameFlow"]["xG"], tooltip: this.$t("table.gameFlow.xGTooltip"), width: "10%", align: 'center', value: 'xG', sortable: true }
    ]
    this.generateGameFlowText();
    this.mediaModeData.subtitle = this.game.leagueName;
    this.mediaModeData.subtitle2 = this.$t("game.header.gameFlow");
    this.mediaModeData.titleLeft = this.game.homeTeamName;
    this.mediaModeData.titleRight = this.game.awayTeamName;

    this.mediaModeData.sponsor.src = require(`@/assets/img/driblab_normal_color.png`);
    this.mediaModeData.sponsor.fallBackSrc = require(`@/assets/img/driblab_normal_color.png`);
    this.mediaModeFields = _.map(this.mediaModeFields,(f) => {
      if(f.home){
        f.labelParams = [this.game.homeTeamName];
      }
      if(f.away){
        f.labelParams = [this.game.awayTeamName];
      }
      return f;
    })
    this.$forceUpdate();
  },
  created() {
    window.addEventListener("resize", this.onWindowResize);
  },
  methods: {
    /* This function just call round function from functions.js */
    toRound(value, decimals) {
      return round(value, decimals);
    },
    getBackgroundColor(item, property, inverted = false){
      let allData = _.map(this.gameFlowDataPerPhasesItems,(t) => t[property]);
      let max = _.max(allData);
      let data = item[property];
      let percentageOfColor = data / max;
      if(inverted){
        percentageOfColor = 1 - percentageOfColor;
      }
      return `rgba(51, 102, 255,${percentageOfColor})`
    },
    getTextColorClass(item, property, inverted = false){
      let allData = _.map(this.gameFlowDataPerPhasesItems,(t) => t[property]);
      let max = _.max(allData);
      let percentageOfColor = item[property] / max;
              if(inverted){
          percentageOfColor = 1 - percentageOfColor;
        }
      return percentageOfColor > 0.6 ? 'white--text' : 'primary--text';
    },
    async generateGameFlowText(){
      let text = "";
      let moreColor = this.homeColor; //Same blue as xT chart home team
      let lessColor = this.awayColor; //Black as xT chart away team
      //Sum of xT
      let moreXTValueTeamName = "";
      let lessXTValueTeamName = "";
      let moreXTValue = 0;
      let lessXTValue = 0;
      let equalsXTValues = false;
      //Sum of minutes xT
      let moreXTTimeTeamName = "";
      let lessXTTimeTeamName = "";
      let moreXTTime = 0;
      let lessXTTime = 0;
      let equalsXTTime = false;
      //Max xT generated
      let maxXTHomeTeamName = "";
      let maxXTAwayTeamName = "";
      let maxXTHomeTeamValue = 0;
      let maxXTHomeTeamMinute = 0;
      let maxXTAwayTeamValue = 0;
      let maxXTAwayTeamMinute = 0;
      let equalsMaxXTValues = false;
      //Home variables
      let xTTotalHomeTeam = this.gameFlowDataPerPhasesItems
                            .filter(gameFlowItem => gameFlowItem.teamId === this.game.homeTeamId)
                            .reduce((result, a) => result + a.xT, 0);
      let minutesTotalHomeTeam = 0;
      let homeGoalsAccum = 0;
      let homePlayerNamesWhoScoreGoalArray = [];
      //Away variables
      let xTTotalAwayTeam = this.gameFlowDataPerPhasesItems
                            .filter(gameFlowItem => gameFlowItem.teamId === this.game.awayTeamId)
                            .reduce((result, a) => result + a.xT, 0);
      let minutesTotalAwayTeam = 0;
      let awayGoalsAccum = 0;
      let awayPlayerNamesWhoScoreGoalArray = [];

      this.chartData.map(min => {
        //1.- Sum xT of each team
        if(min.xT > 0){
          //Total minutes Home Team has xT
          minutesTotalHomeTeam++;
          //Get max xT of the home team
          if(min.xT > maxXTHomeTeamValue){
            maxXTHomeTeamValue = min.xT;
            maxXTHomeTeamMinute = min.minute;
          }
        }else{
          //Total minutes Away Team has xT
          minutesTotalAwayTeam++;
          //Get max xT of the away team
          if(Math.abs(min.xT) > maxXTAwayTeamValue){
            maxXTAwayTeamValue = Math.abs(min.xT);
            maxXTAwayTeamMinute = min.minute;
          }

        }
        //Check how many goals for each team (if we have name of player, we have a goal)
        if(min.homeGoalPlayerName){
          //Get name of the player who scores the goal for the home team
          homePlayerNamesWhoScoreGoalArray.push(min.homeGoalPlayerName);
          homeGoalsAccum++;
          //Check if there is second goal at the same minute
          if(min.homeSecondGoalPlayerName){
            homePlayerNamesWhoScoreGoalArray.push(min.homeSecondGoalPlayerName);
            homeGoalsAccum++;
          }

        }
        if(min.awayGoalPlayerName){
          //Get name of the player who scores the goal for the home team
          awayPlayerNamesWhoScoreGoalArray.push(min.awayGoalPlayerName);
          awayGoalsAccum++;
          //Check if there is second goal at the same minute
          if(min.awaySecondGoalPlayerName){
            awayPlayerNamesWhoScoreGoalArray.push(min.awaySecondGoalPlayerName);
            awayGoalsAccum++;
          }

        }

      });

      //1.- First phase of the match
      if(this.phase === 1){
        text = this.getTextFromDictionary(this.game.gameId, Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["step3"]["text"]["phase"]) //`game.gameAnalysis.step3.text.phase`
                .replace("phase_position", Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["step3"]["text"]["first"]);
      }
      //Last phase of the match
      if(this.phase === this.totalNumberOfPhases){
        text = this.getTextFromDictionary(this.game.gameId, Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["step3"]["text"]["phase"])
                .replace("phase_position", Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["step3"]["text"]["last"]);
      }

      //2.- Check xT total generated
      if(xTTotalAwayTeam === xTTotalHomeTeam){
        //Equals xT generated
        equalsXTValues = true;
        //Same xT Values (Unusual case)
      }else if(xTTotalHomeTeam > xTTotalAwayTeam){
        //Home team has generated more xT, then take home team name as team name with more xT and xT Value as more xT Value
        moreXTValueTeamName = this.game.homeTeamName;
        moreXTValue = xTTotalHomeTeam;
        lessXTValueTeamName = this.game.awayTeamName;
        lessXTValue = xTTotalAwayTeam;
      }else{
        //Away Team has generated more xT
        moreXTValueTeamName = this.game.awayTeamName;
        moreXTValue = xTTotalAwayTeam;
        lessXTValueTeamName = this.game.homeTeamName;
        lessXTValue = xTTotalHomeTeam;
        moreColor = this.awayColor;
        lessColor = this.homeColor;
      }

      //If have not generated same xT
      if(!equalsXTValues){
        text += this.getTextFromDictionary(this.game.gameId, Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["step3"]["text"]["moreXT"]) //`game.gameAnalysis.step3.text.moreXT`
                .replaceAll("less_color", lessColor)
                .replaceAll("more_color", moreColor)
                .replace("more_xt_team_name", moreXTValueTeamName)
                .replace("more_xt_team_value", round(moreXTValue, 2))
                .replace("less_xt_team_name", lessXTValueTeamName)
                .replace("less_xt_team_value", round(lessXTValue, 2));

      }

      //3.- Check time of xT generated
      if(minutesTotalHomeTeam === minutesTotalAwayTeam){
        //Same time generating xT
        equalsXTTime = true;
        //Same number of minutes generating xT (Unusual case)
      }else if(minutesTotalHomeTeam > minutesTotalAwayTeam){
        //Home team has generated during more minutes xT, then take home team name as team name with more xT and xT Value as more xT Value
        moreXTTimeTeamName = this.game.homeTeamName;
        moreXTTime = minutesTotalHomeTeam;
        lessXTTimeTeamName = this.game.awayTeamName;
        lessXTTime = minutesTotalAwayTeam;
        moreColor = this.homeColor;
        lessColor = this.awayColor;
      }else{
        //Away Team has generated more xT
        moreXTTimeTeamName = this.game.awayTeamName;
        moreXTTime = minutesTotalAwayTeam;
        lessXTTimeTeamName = this.game.homeTeamName;
        lessXTTime = minutesTotalHomeTeam;
        moreColor = this.awayColor;
        lessColor = this.homeColor;
      }
      //Set ',' between one text and the second one
      text += Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["comma"];
      //If have not generated xT during same time
      if(!equalsXTTime){
        if(moreXTTimeTeamName === moreXTValueTeamName){
          //Same team has generated more xT during more time (minutes).
          text += this.getTextFromDictionary(this.game.gameId, Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["step3"]["text"]["sameTeamMoreMinutes"]); //`game.gameAnalysis.step3.text.sameTeamMoreMinutes`
        }else{
          //One team has generated more xT value but his rival has generated xT during more time (minutes).
          text += this.getTextFromDictionary(this.game.gameId, Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["step3"]["text"]["rivalTeamMoreMinutes"]); //`game.gameAnalysis.step3.text.rivalTeamMoreMinutes`
        }
        //Replace values (total time of each team generating xT)
        text = text.replace("more_time_xt_minutes", moreXTTime)
               .replaceAll("less_color", lessColor)
               .replaceAll("more_color", moreColor)
               .replace("less_time_xt_minutes", lessXTTime);

      }

      //Set '.' and break line
      text += Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["dot"];
      text += "</br>";
      text += "</br>";
      let maxXTTeamName;
      let maxXTTeamValue;
      let maxXTTeamMinute;
      let maxColor;
      let maxRivalColor;
      let maxXTRivalTeamName;
      let maxXTRivalTeamValue;
      let maxXTRivalTeamMinute;
      //4.- Check MAX xT generated
      if(maxXTHomeTeamValue === maxXTAwayTeamValue){
        //Same max xT generated
        equalsMaxXTValues = true;
      }else if(maxXTHomeTeamValue > maxXTAwayTeamValue){
        //Home team has the max xT generated in this phase
        maxColor = this.homeColor;
        maxRivalColor = this.awayColor;
        maxXTTeamName = this.game.homeTeamName;
        maxXTRivalTeamName = this.game.awayTeamName;
        maxXTTeamValue = round(maxXTHomeTeamValue, 2);
        maxXTRivalTeamValue = round(maxXTAwayTeamValue, 2);
        maxXTTeamMinute = maxXTHomeTeamMinute;
        maxXTRivalTeamMinute = maxXTAwayTeamMinute;
      }else{
        //Away team has the max xT generated in this phase
        maxColor = this.awayColor;
        maxRivalColor = this.homeColor;
        maxXTTeamName = this.game.awayTeamName;
        maxXTRivalTeamName = this.game.homeTeamName;
        maxXTTeamValue = round(maxXTAwayTeamValue, 2);
        maxXTRivalTeamValue = round(maxXTHomeTeamValue, 2);
        maxXTTeamMinute = maxXTAwayTeamMinute;
        maxXTRivalTeamMinute = maxXTHomeTeamMinute;
      }
      //If have not generated same max xT
      if(!equalsMaxXTValues){
        text += this.getTextFromDictionary(this.game.gameId, Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["step3"]["text"]["maxXTValue"]) //`game.gameAnalysis.step3.text.maxXTValue`
                .replaceAll("max_color", maxColor)
                .replace("max_xt_team_name", maxXTTeamName)
                .replace("max_xt_team_value", maxXTTeamValue)
                .replace("max_xt_team_minute", maxXTTeamMinute);
        //If max xT of the rival is greater than 0, add text
        if(maxXTRivalTeamValue > 0){
          text += this.getTextFromDictionary(this.game.gameId, Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["step3"]["text"]["maxXTValueRival"]) //`game.gameAnalysis.step3.text.maxXTValueRival`
                  .replaceAll("max_rival_color", maxRivalColor)
                  .replace("max_xt_rival_team_name", maxXTRivalTeamName)
                  .replace("max_xt_rival_team_value", maxXTRivalTeamValue)
                  .replace("max_xt_rival_team_minute", maxXTRivalTeamMinute);
        }

      }

      //Set '.' and break line
      text += Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["dot"];
      text += "</br>";
      text += "</br>";

      //5.- Check Goals generated
      if(homeGoalsAccum > 0 || awayGoalsAccum > 0){
        //Some team has scored goals
        if(homeGoalsAccum === awayGoalsAccum){
          let goalText = homeGoalsAccum > 1 ? Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["step3"]["text"]["goals"] : Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["step3"]["text"]["goal"];
          let homePlayerNamesText = this.generatePlayerNamesText(homePlayerNamesWhoScoreGoalArray);
          let awayPlayerNamesText = this.generatePlayerNamesText(awayPlayerNamesWhoScoreGoalArray);
          //Both teams have socred but draw in this period
          text += this.getTextFromDictionary(this.game.gameId, Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["step3"]["text"]["bothTeamsScoreButDraw"])
                  .replace("number_of_goals_each_team", (homeGoalsAccum + goalText))
                  .replace("home_player_name_goals", homePlayerNamesText)
                  .replace("away_player_name_goals", awayPlayerNamesText);

        }else if(homeGoalsAccum > awayGoalsAccum){
          let difference = (homeGoalsAccum - awayGoalsAccum);
          let goalText = difference > 1 ? Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["step3"]["text"]["goals"] : Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["step3"]["text"]["goal"];
          let homePlayerNamesText = this.generatePlayerNamesText(homePlayerNamesWhoScoreGoalArray);
          let scorePlayerText = awayGoalsAccum > 0 ? Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["step3"]["text"]["goalScorers"] : Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["step3"]["text"]["goalScorer"];
          //Home team takes the lead
          text += this.getTextFromDictionary(this.game.gameId, Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["step3"]["text"]["oneTeamTakesTheLead"])
                  .replace("goal_scored_team_name", this.game.homeTeamName)
                  .replaceAll("replace_color", this.homeColor)
                  .replace("difference_number_of_goals", (difference + goalText))
                  .replace("home_player_name_goals", homePlayerNamesText)
                  .replace("goal_scorer_word", scorePlayerText);

        }else{
          //Away team takes the lead
          let difference = (awayGoalsAccum - homeGoalsAccum);
          let goalText = difference > 1 ? Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["step3"]["text"]["goals"] : Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["step3"]["text"]["goal"];
          let awayPlayerNamesText = this.generatePlayerNamesText(awayPlayerNamesWhoScoreGoalArray);
          let scorePlayerText = homeGoalsAccum > 0 ? Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["step3"]["text"]["goalScorers"] : Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["step3"]["text"]["goalScorer"];
          //Away team takes the lead
          text += this.getTextFromDictionary(this.game.gameId, Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["step3"]["text"]["oneTeamTakesTheLead"])
                  .replace("goal_scored_team_name", this.game.awayTeamName)
                  .replaceAll("replace_color", this.awayColor)
                  .replace("difference_number_of_goals", (difference + goalText))
                  .replace("home_player_name_goals", awayPlayerNamesText)
                  .replace("goal_scorer_word", scorePlayerText);
        }

        //Set final '.'
        text += Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["dot"];
      }
      //Add generated text to the xT array text
      this.xTText = text;
    },
    getTextFromDictionary(gameId, dictionaryText){
      let availableText = Object.values(dictionaryText);
      let text = "";
      if(availableText.length > 0){
          //gameId % availableText.length to take always the same description to the same game
          text = availableText[gameId % availableText.length];
      }

      return text;
    },
    generatePlayerNamesText(arrayOfNames){
      let textToReturn = "";
      //Check if more than one player scores a goal
      if(arrayOfNames.length > 1){
        //Remove repeated players
        let removeRepeatedPlayers = [...new Set(arrayOfNames)];
        let removeRepeatedPlayersLength = removeRepeatedPlayers.length;
        //Check if after remove repeated players, there is more than one player
        if(removeRepeatedPlayersLength > 1){
          removeRepeatedPlayers.forEach((item, index) => {
            if(index < (removeRepeatedPlayersLength - 2)){
              textToReturn += item + ", ";
            }else if(index == (removeRepeatedPlayersLength - 2)){
              //Add conjuction before the second to last
              textToReturn += item + Dictionary[this.currentUserLanguage]["game"]["gameAnalysis"]["and"];
            }else{
              //is the last item
              textToReturn += item;
            }
          });
        }else{
          textToReturn = removeRepeatedPlayers.toString();
        }

      }else{
        //Just one player scores goal/s
        textToReturn = arrayOfNames.toString();
      }

      return textToReturn;
    },
    async saveAsPNG() {
      if(this.mediaMode){
        let svg = await this.prepareSvgForMedia();
         downloadPng(svg,`Driblab_Game_Flow_${this.game.homeTeamName.replace(' ','_')}_${this.game.homeTeamScore}_${this.game.awayTeamScore}_${this.game.awayTeamName.replace(' ','_')}.png`);
      }else{
        let svg = await this.prepareSVG();
         downloadPng(svg.node(), `Driblab_Game_Flow_${this.game.homeTeamName.replace(' ','_')}_${this.game.homeTeamScore}_${this.game.awayTeamScore}_${this.game.awayTeamName.replace(' ','_')}.png`);
      }

    },
    async saveAsSVG() {
      if(this.mediaMode){
        let svg = await this.prepareSvgForMedia();
        exportSvg(svg, `Driblab_Game_Flow_${this.game.homeTeamName.replace(' ','_')}_${this.game.homeTeamScore}_${this.game.awayTeamScore}_${this.game.awayTeamName.replace(' ','_')}`);
      }else{
        let svg = await this.prepareSVG();
          exportSvg(svg.node(), `Driblab_Game_Flow_${this.game.homeTeamName.replace(' ','_')}_${this.game.homeTeamScore}_${this.game.awayTeamScore}_${this.game.awayTeamName.replace(' ','_')}`);
      }

    },
    async getElementToExport(){
      if(this.mediaMode){
        return await this.prepareSvgForMedia();
      }else{
        let svg = await this.prepareSVG();
       return svg.node();
      }
    },
    async prepareSVG(){
      let c = document.getElementById(`xTChart${this.gameId}-${this.chartId}`);
      let dims = c.getBoundingClientRect();

      let svg = d3.select(document.createElement("div")).append("svg").attr("width", dims.width)
        .attr("height", dims.height)
        .style("fill", "black")
        .style("background", "transparent")
        .attr("class", "xtChartSVG");

      drawImage(svg,dims.width/2,dims.height/2,dims.width,dims.height,c.toDataURL("image/png"),"xtPng","xtPng");

      let font = await loadFonts();
      copyAndAppendElement(svg.node(),font.node(),0,0);

      return svg;
    },
    async prepareSvgForMedia(){
      let parent = document.createElementNS('http://www.w3.org/2000/svg','svg');
      let heightAccum = 0;
      let yOffset = 10;
      parent.style.background = this.mediaModeData.bgColor;

      //COPY HEADER
      copyAndAppendElement(parent,document.querySelector(`#gameAvatarMedia${this.gameId}-${this.chartId}SVG`),0,yOffset)
      heightAccum += this.avatarSize.height;
      let chartSvg = await this.prepareSVG();
      copyAndAppendElement(parent,chartSvg.node(),0,yOffset + this.avatarSize.height)
      heightAccum += this.chartSize.height;
      if(this.mediaModeData.socialMedia){
        copyAndAppendElement(parent,document.querySelector(`#driblabSocialMedia${this.gameId}-${this.chartId}SVG`),0,yOffset + this.avatarSize.height + this.chartSize.height)
        heightAccum += 50;
      }

      parent.setAttribute("width",this.chartSize.width);
      parent.setAttribute("height",heightAccum + yOffset);
      return parent;
    },
    onMediaModeChange(event){
      this.mediaMode = event;
    },
    onMediaModeDataChange(event){
      this.mediaModeData = {...event};
      this.xTChart.data.datasets[0].label = this.mediaModeData.titleLeft;
      this.xTChart.data.datasets[0].awayTeamName = this.mediaModeData.titleRight;
      setTimeout(() => {this.onWindowResize(null,this.mediaModeData.force);},666)
    },
    onWindowResize(e,force=false) {
      setTimeout(() => {
        force = force || (e && e.type === "resize");
        if(this.$refs.xTChartRef){
          this.$refs.xTChartRef.updateChart();
        }
        if(this.$refs.gameAvatarMediaRef){
          this.$refs.gameAvatarMediaRef.printD3(force)
        }
        if(this.$refs.driblabSocialMediaRef){
          this.$refs.driblabSocialMediaRef.printD3(force)
        }
        this.$forceUpdate();
      },100)
    },
    onChartSizeChange(event){
      this.chartSize = event;
      this.$forceUpdate();
    },
    onLoaded(event){
      //Emit when is loaded
      this.$emit("loaded", event);
    },
    isWhite(color){
      return color === "#FFF" || color === "#fff" || color === "#FFFFFF" || color === "#ffffff" || color === "white";
    },
    async onGenerateIFrameClick(){
      this.loadingExport = true;
      let elToExport = await this.getElementToExport();
      let key = v4();
      key = `${key}.json`;
      let maxHeight = this.mediaMode ? 550 : 360;
      let reductionFactor = this.mediaMode ? 0.75 : 0.55;
      let height = elToExport.getAttribute("width") * reductionFactor;
      height = height < maxHeight ? height : maxHeight;

      let chartObject = {
        awsKey:key,
        tool: 'game-flow',
        category: 'game',
        componentId: this.gameId,
        width: elToExport.getAttribute("width"),
        height: height
      }
      let postResponse = await UserChartService.postChart(chartObject)
      let chartEntity = postResponse.data;
      chartObject.id = chartEntity.id;

      this.$refs.chartExportLinksModalRef.open(chartEntity);
      this.loadingExport = false;

      let urlResponse = await UserChartService.getUploadURL(chartEntity.id,key);
      let uploadUrl = urlResponse.data;
      var jsonse = JSON.stringify({
        size: Number(chartEntity.width),
        height: Number(chartEntity.height),
        datasets: this.xTChart.data.datasets.map(d => {let newD = _.cloneDeep(d);delete newD._meta;return newD}),
        xAxisLabels: this.xTChart.data.labels,
        options: this.xTChart.options,
        mediaModeData: this.mediaModeData,
        mediaMode: this.mediaMode,
        language: this.currentUserLanguage,
        playerAvatarKey: 0,
        key: 1,
        socialMediaKey: 2,
        id: `${this.id}${this.gameId}-${this.chartId}`,
        game:this.game,
        chartType:"game-flow",
        category: 'game',
      });

      var blob = new Blob([jsonse], {type: "application/json"});
      // let blob = exportSvgToFile(elToExport);
      let uploadResponse = await DocumentService.upload(uploadUrl,blob);
      this.postSearchChart(chartEntity);
    },
    async postSearchChart(chart){
      const authorized = true;
      let metaInfo = null;
      let category = "game";
      let urlId = this.gameId;

      metaInfo = JSON.stringify({
        link: `${API_URL}${chart.id}/key/${chart.awsKey}/compId/${chart.componentId}/catId/${chart.categoryId}/tId/${chart.toolId}`
      });
      const tool = 'embed';
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const browser = navigator.userAgent;
      let searchResponse = await UserService.postUserSearch(category, tool, urlId, authorized, this.$route.path, timeZone, browser, metaInfo);
      UserService.putLastSearchRendered({category,tool,id:searchResponse.data})
    },
    userHasRole(role) {
      return userHasRole(this.currentUser, role);
    }
  },
  destroyed() {
    window.removeEventListener("resize", this.onWindowResize);
  },
};
</script>
<style lang="scss">
  #gameFlowTable {
    td, th {
      padding-left: 0px !important;
      padding-right: 0px !important;
    }
  }
</style>
